import React, { useState, useEffect } from 'react';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import OrganizationContainer from '../../../state/containers/OrganizationContainer';
import TemplateConfigurationContainer from '../../../state/containers/TemplateConfigurationContainer';
import Loader from '../../../components/general/Loader';
import PanelHeaderContainer from '../../../components/structure/Panels/PanelHeaderContainer';
import ScrollablePanelBody from '../../../components/structure/ScrollablePanelBody';
import Button from '../../../components/general/Button';
import SearchField from '../../../components/forms/SearchField';
import OrganizationTemplateListItem from './OrganizationTemplateListItem';

interface OrganizationTemplatesListViewProps {
    orgContainer: OrganizationContainer
    templatesContainer: TemplateConfigurationContainer
    history: any
}

const OrganizationTemplatesListView: React.FC<OrganizationTemplatesListViewProps> = (props) => {
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (props.templatesContainer.state.organizationTemplates.length == 0) {
            props.templatesContainer.getOrganizationTemplates(props.orgContainer.state.currentOrganization?.id);
        }
    }, []);

    const handleSearch = (searchEvent: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(searchEvent.target.value);
    }

    const handleAddNew = () => {
        props.history.push('/v/modules/add')
    }

    if (props.templatesContainer.state.isLoading)
        return <Loader />

    const counts = props.templatesContainer.state.appliedTemplateCounts;

    const filteredTemplates = props.templatesContainer.state.organizationTemplates?.filter(template =>
        template.name?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    // Sort templates with three levels:
    // 1. Most active instances first (descending)
    // 2. Last modified date (descending - most recent first)  
    // 3. Alphabetically by name (ascending)
    const sortedTemplates = [...filteredTemplates].sort((a, b) => {
        const aInstanceCount = counts?.find(c => c.templateConfigurationId === a.id)?.appliedTemplateCount || 0;
        const bInstanceCount = counts?.find(c => c.templateConfigurationId === b.id)?.appliedTemplateCount || 0;

        // First level: Sort by active instance count (descending)
        if (aInstanceCount !== bInstanceCount) {
            return bInstanceCount - aInstanceCount;
        }

        // Second level: Sort by modified date (descending - most recent first)
        const aDate = new Date(a.modifiedDate || 0);
        const bDate = new Date(b.modifiedDate || 0);
        if (aDate.getTime() !== bDate.getTime()) {
            return bDate.getTime() - aDate.getTime();
        }

        // Third level: Sort alphabetically by name (ascending)
        const aName = a.name?.toLowerCase() || '';
        const bName = b.name?.toLowerCase() || '';
        return aName.localeCompare(bName);
    });

    return (
        <>
            <PanelHeaderContainer className={panelHeaderStyle}>
                <Title>Modules</Title>
                <ModuleSearchAndAddWrapper>
                    <ModuleSearchBar
                        id="moduleSearchField"
                        className={moduleSearchBarStyle}
                        name="moduleSearchField"
                        placeholder="Search modules"
                        value={searchTerm}
                        onChange={handleSearch}
                        boxType="square"
                    />
                    <Button themes={['primary-small', 'end']} text="Create New Module" onClick={handleAddNew} />
                </ModuleSearchAndAddWrapper>
            </PanelHeaderContainer>
            <ScrollablePanelBody className={panelBodyStyle}>
                {sortedTemplates?.map(t =>
                    <OrganizationTemplateListItem
                        key={t.id}
                        templateConfiguration={t}
                        activeInstanceCount={counts?.find(c => c.templateConfigurationId == t.id)?.appliedTemplateCount} />
                )}
            </ScrollablePanelBody>
        </>
    )
}

const ModuleSearchAndAddWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
`;

const Title = styled.h2``;

const panelHeaderStyle = css`
    padding: 0 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    button {
        margin-right: 0;
        margin-left: auto;
    }
    h2 {
        font-size: 18px;
        line-height: 24px;
        margin-right: auto;
    }
`;

const ModuleSearchBar = styled(SearchField)`
    flex-grow: 1;
    max-width: 300px;
    div > .field-container {
        height: 36px;
        box-shadow: none;
        border-radius: 8px;
        margin: 0;
        padding: 0;
    }
    input::placeholder {
        font-size: 0.75rem;
    }
`;

const moduleSearchBarStyle = css`
    margin-bottom: 0;
`;

const panelBodyStyle = css`
    padding: 48px 0;
    min-height: 100%;
`

export default OrganizationTemplatesListView;